import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Phone, MessageCircle, BadgeCheck, Clock, MapPin,
  ShieldCheck, Truck, HeartPulse, Building2, ChevronRight, CalendarDays,
} from "lucide-react";
import Navbar from "@/components/cima/Navbar";
import Footer from "@/components/cima/Footer";
import WhatsAppFloat from "@/components/cima/WhatsAppFloat";
import { CIMA, openWhatsapp } from "@/lib/cima";

/**
 * Dedicated SEO article page: "Venta de Oxígeno Medicinal en Zihuatanejo"
 * Targets long-tail queries around medical oxygen sale/rental/recharge.
 * Includes Article schema, breadcrumb schema, semantic HTML headings.
 */
export default function ArticleOxigenoMedicinal() {
  useEffect(() => {
    // Update document title & meta for this route (SPA)
    const prevTitle = document.title;
    const desc =
      "Venta de oxígeno medicinal en Zihuatanejo: distribuidor autorizado INFRA, atención 24/7, renta y recarga de tanques a domicilio. Guía completa para familias, clínicas y negocios.";
    document.title =
      "Venta de Oxígeno Medicinal en Zihuatanejo · CIMA · Atención 24/7";
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content");
    metaDesc?.setAttribute("content", desc);

    // Inject Article + Breadcrumb JSON-LD
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "article-jsonld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          headline: "Venta de Oxígeno Medicinal en Zihuatanejo",
          description: desc,
          author: {
            "@type": "Organization",
            name: "CIMA — Comercializadora Industrial Médica",
          },
          publisher: {
            "@type": "Organization",
            name: "CIMA — Comercializadora Industrial Médica",
            logo: {
              "@type": "ImageObject",
              url: "https://cimacomercializadora.com/cima-products/oxigeno-medicinal-recarga.jpg",
            },
          },
          datePublished: "2026-02-01",
          dateModified: new Date().toISOString().slice(0, 10),
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id":
              "https://cimacomercializadora.com/articulos/venta-oxigeno-medicinal-zihuatanejo",
          },
          image:
            "https://cimacomercializadora.com/cima-products/oxigeno-medicinal-recarga.jpg",
          articleSection: "Oxígeno Medicinal",
          keywords:
            "venta de oxígeno medicinal Zihuatanejo, renta de tanque de oxígeno, recarga de oxígeno, distribuidor INFRA Zihuatanejo, atención 24/7, oxígeno a domicilio",
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Inicio", item: "https://cimacomercializadora.com/" },
            { "@type": "ListItem", position: 2, name: "Artículos", item: "https://cimacomercializadora.com/articulos" },
            {
              "@type": "ListItem",
              position: 3,
              name: "Venta de Oxígeno Medicinal en Zihuatanejo",
              item: "https://cimacomercializadora.com/articulos/venta-oxigeno-medicinal-zihuatanejo",
            },
          ],
        },
      ],
    });
    document.head.appendChild(ld);

    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      if (prevDesc) metaDesc?.setAttribute("content", prevDesc);
      document.getElementById("article-jsonld")?.remove();
    };
  }, []);

  return (
    <div data-testid="article-page" className="min-h-screen bg-white text-slate-900">
      <Navbar onOpenQuote={() => {}} />

      {/* Hero */}
      <header className="relative pt-28 lg:pt-32 pb-12 lg:pb-16 bg-[var(--cima-navy)] text-white overflow-hidden">
        <div className="absolute inset-0 stripe-accent opacity-10" />
        <div className="absolute -top-20 -left-20 w-80 h-80 stripe-accent opacity-15 rotate-12" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="font-mono-cima text-[10px] uppercase tracking-[0.3em] text-white/60 flex items-center gap-2 flex-wrap">
            <Link to="/" className="hover:text-white transition-colors">Inicio</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/40">Artículos</span>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white">Oxígeno Medicinal</span>
          </nav>

          <div className="mt-6 inline-flex items-center gap-2 bg-white/10 backdrop-blur px-3 py-1.5 border border-white/20">
            <HeartPulse className="w-3.5 h-3.5 text-[var(--cima-orange)]" />
            <span className="font-mono-cima text-[10px] uppercase tracking-[0.3em]">Guía · Oxígeno Medicinal</span>
          </div>

          <h1 className="mt-5 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
            Venta de Oxígeno Medicinal en{" "}
            <span className="text-[var(--cima-orange)]">Zihuatanejo</span>:
            cómo elegir bien y resolver rápido.
          </h1>

          <p className="mt-5 text-white/85 text-base lg:text-lg max-w-2xl leading-relaxed">
            Producto confiable, atención 24/7 y respaldo como{" "}
            <strong className="text-white">Distribuidor Autorizado INFRA</strong>. Una
            guía clara para familias, clínicas y negocios que necesitan oxígeno medicinal
            sin improvisar.
          </p>

          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <a
              href={`tel:${CIMA.phoneTel}`}
              data-testid="article-cta-call"
              className="inline-flex items-center justify-center gap-2 bg-[var(--cima-red)] hover:bg-[var(--cima-red-deep)] text-white px-6 py-3.5 font-display font-bold text-sm uppercase tracking-wide transition-colors"
            >
              <Phone className="w-4 h-4" />
              Llamar al {CIMA.phone}
            </a>
            <button
              type="button"
              data-testid="article-cta-whatsapp"
              onClick={() =>
                openWhatsapp(
                  "Hola CIMA, leí su artículo sobre oxígeno medicinal y quiero más información sobre venta/renta/recarga."
                )
              }
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white/40 hover:bg-white hover:text-[var(--cima-navy)] text-white px-6 py-3.5 font-display font-bold text-sm uppercase tracking-wide transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </button>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-white/70 font-mono-cima uppercase tracking-widest">
            <span className="inline-flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5 text-[var(--cima-orange)]" /> Distribuidor INFRA</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[var(--cima-orange)]" /> Atención 24/7</span>
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[var(--cima-orange)]" /> Zihuatanejo · Costa Grande</span>
            <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-[var(--cima-orange)]" /> Desde 1972</span>
          </div>
        </div>
      </header>

      {/* Article body */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        {/* Lead paragraph */}
        <p className="text-lg lg:text-xl text-slate-800 leading-relaxed font-display">
          Cuando un paciente necesita apoyo respiratorio en casa, no hay margen para
          improvisar. En la venta de oxígeno medicinal lo que realmente pesa no es solo
          el cilindro disponible, sino la certeza de recibir un suministro seguro,
          continuo y atendido por un proveedor que responda de inmediato en{" "}
          <strong>Zihuatanejo</strong>.
        </p>

        <p className="mt-5 text-slate-700 leading-relaxed">
          Esa diferencia se nota desde el primer contacto. Hay casos en los que una
          familia necesita oxígeno por alta hospitalaria, otros en los que un paciente
          crónico requiere continuidad todos los días, y también situaciones en las que
          un negocio del sector salud no puede quedarse sin inventario. En todos esos
          escenarios la prioridad es la misma: resolver rápido, con producto confiable
          y sin errores en la logística.
        </p>

        {/* Section 1 */}
        <h2 className="mt-12 font-display font-extrabold text-2xl sm:text-3xl text-[var(--cima-navy)] leading-tight">
          Qué debe ofrecer una venta de oxígeno medicinal confiable
        </h2>
        <p className="mt-4 text-slate-700 leading-relaxed">
          La venta de oxígeno medicinal no se reduce a entregar un tanque. Debe incluir
          control en el manejo, claridad sobre la modalidad de suministro y capacidad
          real de respuesta. Cuando el servicio es serio, el cliente sabe qué está
          recibiendo, en qué presentación, bajo qué condiciones y con qué respaldo.
        </p>
        <p className="mt-4 text-slate-700 leading-relaxed">
          Un punto clave es la <strong>calidad del producto</strong>. En uso medicinal,
          el oxígeno debe cumplir con estándares específicos porque está destinado al
          cuidado de la salud. Por eso conviene tratar con un{" "}
          <strong>distribuidor autorizado</strong> y con operación formal, no con
          opciones improvisadas o sin trazabilidad. La confianza aquí no es un extra —
          es parte del servicio.
        </p>
        <p className="mt-4 text-slate-700 leading-relaxed">
          También importa la disponibilidad. Un proveedor puede tener buen precio, pero
          si no responde por la noche, en fin de semana o cuando se requiere una recarga
          urgente, el servicio queda corto. En esta categoría, la{" "}
          <strong>capacidad de atención continua</strong> cambia por completo la
          experiencia del cliente.
        </p>

        {/* Section 2 */}
        <h2 className="mt-12 font-display font-extrabold text-2xl sm:text-3xl text-[var(--cima-navy)] leading-tight">
          Oxígeno medicinal para casa, clínica o negocio
        </h2>
        <p className="mt-4 text-slate-700 leading-relaxed">
          No todos los clientes necesitan lo mismo, y por eso el servicio debe
          adaptarse. <strong>En casa</strong>, normalmente la prioridad es contar con un
          cilindro listo para uso inmediato, una explicación clara sobre su manejo y
          una ruta rápida para recarga o reposición. Para muchas familias, esa
          combinación reduce presión en un momento sensible.
        </p>
        <p className="mt-4 text-slate-700 leading-relaxed">
          En <strong>clínicas, consultorios o espacios de atención médica</strong>, la
          necesidad suele centrarse en continuidad operativa. Aquí no basta con una
          entrega puntual. Se requiere orden en el suministro, tiempos de respuesta
          definidos y la tranquilidad de trabajar con un proveedor que entienda que una
          interrupción afecta directamente la atención al paciente.
        </p>
        <p className="mt-4 text-slate-700 leading-relaxed">
          En el caso de <strong>empresas o negocios relacionados con servicios técnicos
          y comerciales</strong>, la lógica cambia un poco. Aunque el oxígeno medicinal
          tiene un uso específico, muchos compradores también necesitan coordinar otros
          gases en el mismo proveedor. Tener un solo punto de atención para distintos
          requerimientos simplifica compras, reduce tiempos y mejora el control de
          abastecimiento.
        </p>

        {/* Highlight callout */}
        <aside className="mt-10 border-l-4 border-[var(--cima-red)] bg-[var(--cima-surface)] p-6 lg:p-7">
          <p className="font-display font-semibold text-[var(--cima-navy)] text-base lg:text-lg leading-snug">
            ¿Necesitas oxígeno medicinal hoy mismo?
          </p>
          <p className="mt-2 text-sm text-slate-700 leading-relaxed">
            Recargamos tu tanque portátil <strong>el mismo día y en minutos</strong>.
            Atención telefónica 24/7 al{" "}
            <a href={`tel:${CIMA.phoneTel}`} className="text-[var(--cima-red)] font-semibold underline">
              {CIMA.phone}
            </a>
            .
          </p>
        </aside>

        {/* Section 3 */}
        <h2 className="mt-12 font-display font-extrabold text-2xl sm:text-3xl text-[var(--cima-navy)] leading-tight">
          Comprar, rentar o recargar: cuál opción conviene
        </h2>
        <p className="mt-4 text-slate-700 leading-relaxed">
          Depende del tiempo de uso, la frecuencia de consumo y la urgencia de la
          necesidad. Si el requerimiento será continuo o de largo plazo, la{" "}
          <strong>compra</strong> puede ser conveniente para mantener autonomía
          operativa. Si el uso será temporal, la <strong>renta</strong> suele ser una
          alternativa práctica porque evita una inversión mayor al inicio.
        </p>
        <p className="mt-4 text-slate-700 leading-relaxed">
          La <strong>recarga</strong>, por su parte, es indispensable para quienes ya
          cuentan con cilindro y solo necesitan mantenerlo en operación. Aquí el factor
          crítico es la rapidez. Un servicio de recarga eficiente no solo repone el
          contenido — ayuda a que el paciente o el negocio no enfrenten pausas
          innecesarias.
        </p>
        <p className="mt-4 text-slate-700 leading-relaxed">
          Hay casos en los que la mejor opción es combinar modalidades. Por ejemplo,
          una familia puede iniciar con renta por una etapa de recuperación y después
          ajustar según evolución médica. En cambio, un cliente institucional puede
          preferir compra o un esquema recurrente de suministro. Lo importante es que
          el proveedor explique con claridad qué alternativa resuelve mejor cada
          situación.
        </p>

        {/* Section 4 — Checklist */}
        <h2 className="mt-12 font-display font-extrabold text-2xl sm:text-3xl text-[var(--cima-navy)] leading-tight">
          Qué revisar antes de contratar el servicio
        </h2>
        <p className="mt-4 text-slate-700 leading-relaxed">
          Antes de decidir, conviene revisar cuatro aspectos que realmente impactan el
          resultado:
        </p>
        <ol className="mt-5 space-y-4">
          {[
            {
              t: "Respaldo del proveedor",
              d: (
                <>
                  Trabajar con un <strong>Distribuidor Autorizado INFRA</strong> aporta
                  un nivel de confianza importante en una categoría donde la calidad y
                  la seguridad no admiten dudas.
                </>
              ),
              icon: BadgeCheck,
            },
            {
              t: "Cobertura local",
              d: (
                <>
                  En una necesidad médica la cercanía operativa sí importa. Tener
                  atención en <strong>Zihuatanejo</strong> permite responder con mayor
                  rapidez, coordinar entregas a domicilio y atender incidencias sin
                  depender de rutas lejanas.
                </>
              ),
              icon: MapPin,
            },
            {
              t: "Disponibilidad real 24/7",
              d: (
                <>
                  Muchas empresas dicen atender urgencias, pero no todas sostienen ese
                  servicio de forma continua. Si el suministro es crítico, confirma que
                  exista atención permanente.
                </>
              ),
              icon: Clock,
            },
            {
              t: "Claridad comercial",
              d: (
                <>
                  Un servicio confiable explica la diferencia entre venta, renta y
                  recarga; informa sobre disponibilidad y orienta sin rodeos. Eso es
                  especialmente valioso cuando quien compra no está familiarizado con
                  el manejo de gases medicinales.
                </>
              ),
              icon: ShieldCheck,
            },
          ].map((it, i) => {
            const Icon = it.icon;
            return (
              <li key={i} className="flex gap-4 bg-white border border-slate-200 p-5">
                <div className="w-10 h-10 shrink-0 bg-[var(--cima-navy)] text-white flex items-center justify-center font-display font-bold text-sm">
                  {i + 1}
                </div>
                <div>
                  <h3 className="font-display font-bold text-[var(--cima-navy)] text-base flex items-center gap-2">
                    <Icon className="w-4 h-4 text-[var(--cima-red)]" />
                    {it.t}
                  </h3>
                  <p className="mt-1 text-sm text-slate-700 leading-relaxed">{it.d}</p>
                </div>
              </li>
            );
          })}
        </ol>

        {/* Section 5 */}
        <h2 className="mt-12 font-display font-extrabold text-2xl sm:text-3xl text-[var(--cima-navy)] leading-tight">
          Seguridad en el manejo y continuidad del suministro
        </h2>
        <p className="mt-4 text-slate-700 leading-relaxed">
          En oxígeno medicinal, la seguridad empieza desde la entrega. El cilindro debe
          transportarse y colocarse de forma adecuada, y el cliente necesita
          instrucciones claras para su uso y resguardo. No se trata de saturar con
          términos técnicos, sino de asegurar que la operación diaria sea correcta.
        </p>
        <p className="mt-4 text-slate-700 leading-relaxed">
          También es importante mantener el cilindro en condiciones adecuadas, evitar
          manipulación incorrecta y prever la siguiente recarga antes de que el
          suministro llegue al límite. En pacientes con uso continuo, esperar al último
          momento puede complicar innecesariamente una situación ya delicada.
        </p>
        <p className="mt-4 text-slate-700 leading-relaxed">
          Por eso el valor de un proveedor local no está solo en vender. Está en
          sostener la continuidad. Cuando hay <strong>atención a domicilio</strong>,
          seguimiento operativo y respuesta constante, el cliente gana tranquilidad y
          reduce riesgos logísticos.
        </p>

        {/* Section 6 */}
        <h2 className="mt-12 font-display font-extrabold text-2xl sm:text-3xl text-[var(--cima-navy)] leading-tight">
          Por qué el servicio local hace una diferencia real en Zihuatanejo
        </h2>
        <p className="mt-4 text-slate-700 leading-relaxed">
          En una ciudad como <strong>Zihuatanejo</strong>, la velocidad de respuesta
          tiene un impacto directo. Un servicio local puede coordinar entregas con más
          precisión, atender cambios de última hora y resolver con cercanía. Eso
          beneficia tanto a familias como a clínicas, restaurantes, talleres o negocios
          que trabajan con distintos gases y requieren un proveedor presente.
        </p>
        <p className="mt-4 text-slate-700 leading-relaxed">
          Además, la atención cercana permite entender mejor el contexto del cliente.
          No es lo mismo surtir a una familia en recuperación que a un establecimiento
          con operación continua. Un proveedor serio ajusta la atención según la
          urgencia, el volumen y la frecuencia de consumo.
        </p>
        <p className="mt-4 text-slate-700 leading-relaxed">
          <strong>CIMA Comercializadora Industrial Médica</strong> opera bajo esa
          lógica: atención local inmediata, disponibilidad continua y respaldo como
          Distribuidor Autorizado INFRA. Para quien necesita resolver rápido y con
          seguridad, ese tipo de estructura operativa sí hace diferencia.
        </p>

        {/* Section 7 */}
        <h2 className="mt-12 font-display font-extrabold text-2xl sm:text-3xl text-[var(--cima-navy)] leading-tight">
          Venta de oxígeno medicinal con atención 24/7
        </h2>
        <p className="mt-4 text-slate-700 leading-relaxed">
          La urgencia no siempre llega en horario de oficina. Por eso, en la venta de
          oxígeno medicinal, la disponibilidad permanente es una condición de servicio,
          no un detalle adicional. Si un paciente requiere reposición, si una familia
          necesita entrega a domicilio o si una unidad de atención médica enfrenta una
          necesidad imprevista, la respuesta debe ser inmediata.
        </p>
        <p className="mt-4 text-slate-700 leading-relaxed">
          Un proveedor preparado para atención 24/7 no solo contesta. Tiene capacidad
          de coordinar, surtir y dar continuidad. Esa es la diferencia entre una
          promesa comercial y una operación confiable.
        </p>
        <p className="mt-4 text-slate-700 leading-relaxed">
          Al final, elegir bien un proveedor de oxígeno medicinal es decidir qué tan
          respaldado quieres estar cuando el tiempo importa. Si el servicio combina{" "}
          <strong>producto confiable, cobertura local, renta, recarga, entrega a
          domicilio y respuesta permanente</strong>, la decisión se vuelve mucho más
          clara.
        </p>

        {/* Final CTA */}
        <div className="mt-14 bg-[var(--cima-navy)] text-white p-8 lg:p-10 relative overflow-hidden">
          <div className="absolute inset-0 stripe-accent opacity-10" />
          <div className="relative">
            <span className="font-mono-cima text-[10px] uppercase tracking-[0.3em] text-[var(--cima-orange)]">
              Atención inmediata
            </span>
            <h2 className="mt-2 font-display font-extrabold text-2xl lg:text-3xl leading-tight">
              ¿Necesitas oxígeno medicinal en Zihuatanejo?
            </h2>
            <p className="mt-3 text-white/85 max-w-2xl leading-relaxed">
              Te atendemos al momento. Venta, renta y recarga de tanques portátiles —
              entrega a domicilio en Zihuatanejo, Ixtapa y zona conurbada. Distribuidor
              Autorizado INFRA desde 1972.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <a
                href={`tel:${CIMA.phoneTel}`}
                data-testid="article-final-call"
                className="inline-flex items-center justify-center gap-2 bg-[var(--cima-red)] hover:bg-[var(--cima-red-deep)] text-white px-6 py-3.5 font-display font-bold text-sm uppercase tracking-wide transition-colors"
              >
                <Phone className="w-4 h-4" />
                {CIMA.phone}
              </a>
              <button
                type="button"
                data-testid="article-final-whatsapp"
                onClick={() =>
                  openWhatsapp(
                    "Hola CIMA, necesito información sobre venta/renta/recarga de oxígeno medicinal."
                  )
                }
                className="inline-flex items-center justify-center gap-2 bg-white text-[var(--cima-navy)] hover:bg-[var(--cima-orange)] hover:text-white px-6 py-3.5 font-display font-bold text-sm uppercase tracking-wide transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp directo
              </button>
              <Link
                to="/catalogo"
                data-testid="article-catalog-link"
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white/30 hover:border-white text-white px-6 py-3.5 font-display font-bold text-sm uppercase tracking-wide transition-colors"
              >
                Ver catálogo
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Back to home */}
        <div className="mt-10">
          <Link
            to="/"
            data-testid="article-back-home"
            className="inline-flex items-center gap-2 text-sm font-mono-cima uppercase tracking-widest text-slate-500 hover:text-[var(--cima-red)] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Link>
        </div>
      </article>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
